import React, {FormEvent, useRef, useState} from "react";
import {useLocation, useNavigate} from "react-router-dom";
import "../../../styles/components/sign-in/sign-in-page.scss";
import TextBox from "../../common/textBox/TextBox";
import Button from "../../common/Button";
import {appRoutePaths} from "../../../config/config";
import playgroundApi from "../../../services/playgroundApi";
import {userAuthentication} from "../../../services/user/authentication";
import {recaptchaV3} from "../../../services/signIn/recaptchaV3";
import IAuthenticationResponse from "../../../client/models/authentication/IAuthenticationResponse";
import {socialLinkErrors} from "./socialLinkErrors";
import {SocialLinkHandoff, socialLinkHandoff} from "../../../services/signIn/socialLinkHandoff";
import {SocialSignInProvider} from "../../../client/models/authentication/SocialSignInProvider";

import PngSm from "../images/tapping-tablet-on-keyboard-sm.png";
import PngLg from "../images/tapping-tablet-on-keyboard-lg.png";
import WebpSm from "../images/tapping-tablet-on-keyboard-sm.webp";
import WebpLg from "../images/tapping-tablet-on-keyboard-lg.webp";
import AvifSm from "../images/tapping-tablet-on-keyboard-sm.avif";
import AvifLg from "../images/tapping-tablet-on-keyboard-lg.avif";

function receiptEmailPrefill(email: string | null): string {
  if (email === null) {
    return "";
  }
  return email.trim();
}

export interface ISocialLinkEmailLocationState {
  linkingToken: string;
  prefillEmail: string | null;
  isPrefillEmailVerified?: boolean | null;
  provider?: SocialSignInProvider;
  routeAfterSignIn?: string;
}

export default function SocialLinkEmailPage() {
  const location = useLocation();
  const routerNavigate = useNavigate();

  const routerState = (location.state ?? {}) as Partial<ISocialLinkEmailLocationState>;
  const restored = socialLinkHandoff.restore();

  const linkingToken = routerState.linkingToken ?? restored?.linkingToken ?? null;
  const prefillEmail = routerState.prefillEmail ?? restored?.prefillEmail ?? null;
  const isPrefillEmailVerified = routerState.isPrefillEmailVerified ?? restored?.isPrefillEmailVerified ?? null;
  const provider = routerState.provider ?? restored?.provider ?? null;
  const routeAfterSignIn = routerState.routeAfterSignIn ?? restored?.routeAfterSignIn;

  const [email, setEmail] = useState(receiptEmailPrefill(prefillEmail));
  const [error, setError] = useState<string | null>(null);
  const [endedMessage, setEndedMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const isSendInFlight = useRef(false);

  if (!linkingToken || provider === null || endedMessage !== null) {
    return (
      <div className="sign-in-page">
        <header className="top">
          <picture>
            <source media={"(orientation: portrait)"} srcSet={AvifSm} type={"image/avif"} />
            <source media={"(orientation: portrait)"} srcSet={WebpSm} type={"image/webp"} />
            <source media={"(orientation: portrait)"} srcSet={PngSm} type={"image/png"} />
            <source media={"(orientation: landscape)"} srcSet={AvifLg} type={"image/avif"} />
            <source media={"(orientation: landscape)"} srcSet={WebpLg} type={"image/webp"} />
            <source media={"(orientation: landscape)"} srcSet={PngLg} type={"image/png"} />
            <img src={PngLg} alt="Hand reaching to tap tablet on keyboard" width={740} height={673} className={"image"} />
          </picture>
          <h1 className="overlay-text">Email for communications</h1>
        </header>
        <div className="content-and-navigation">
          <div className="content">
            <div className="page-header-text">Email for communications</div>
            <p className="instructions">{endedMessage ?? "Your sign-in session has expired. Please start over."}</p>
            <Button
              buttonType={"secondary"}
              onClick={() => {
                socialLinkHandoff.clear();
                routerNavigate(appRoutePaths.signInPage);
              }}
              text={"Back to sign in"}
            />
          </div>
        </div>
      </div>
    );
  }

  const signInProvider = provider;

  async function handleContinue(e?: FormEvent) {
    if (e) {
      e.preventDefault();
    }
    // Button is type="submit" and also calls onClick, so click and submit
    // both reach here before isLoading can take effect.
    if (isSendInFlight.current) {
      return;
    }
    isSendInFlight.current = true;
    setError(null);
    setIsLoading(true);
    let didLeavePage = false;

    try {
      let recaptchaToken: string;
      try {
        recaptchaToken = await recaptchaV3.execute("request_email_otc");
      } catch {
        setError(socialLinkErrors.mapErrorToMessage("recaptcha_failed"));
        return;
      }

      const result = await playgroundApi.socialLinkRequestEmailOtc({
        linkingToken: linkingToken!,
        email,
        recaptchaToken,
      });

      if ("error" in result) {
        if (socialLinkErrors.isTerminalError(result.error)) {
          socialLinkHandoff.clear();
          setEndedMessage(socialLinkErrors.mapErrorToMessage(result.error));
          return;
        }
        setError(socialLinkErrors.mapErrorToMessage(result.error));
        return;
      }

      if ("next" in result) {
        const handoff: SocialLinkHandoff = {
          linkingToken: linkingToken!,
          prefillEmail,
          isPrefillEmailVerified,
          provider: signInProvider,
          enteredEmail: email,
          ...(routeAfterSignIn ? {routeAfterSignIn} : {}),
        };
        socialLinkHandoff.persist(handoff);
        routerNavigate(appRoutePaths.socialLinkCode, {state: handoff});
        didLeavePage = true;
        return;
      }

      socialLinkHandoff.clear();
      await userAuthentication.completeSocialSignIn(result as IAuthenticationResponse, signInProvider);
      didLeavePage = true;
      routerNavigate(socialLinkHandoff.pageAfterSignIn(routeAfterSignIn));
    } catch {
      setError(socialLinkErrors.mapErrorToMessage("request_failed"));
    } finally {
      if (!didLeavePage) {
        isSendInFlight.current = false;
        setIsLoading(false);
      }
    }
  }

  return (
    <div className="sign-in-page">
      <header className="top">
        <picture>
          <source media={"(orientation: portrait)"} srcSet={AvifSm} type={"image/avif"} />
          <source media={"(orientation: portrait)"} srcSet={WebpSm} type={"image/webp"} />
          <source media={"(orientation: portrait)"} srcSet={PngSm} type={"image/png"} />
          <source media={"(orientation: landscape)"} srcSet={AvifLg} type={"image/avif"} />
          <source media={"(orientation: landscape)"} srcSet={WebpLg} type={"image/webp"} />
          <source media={"(orientation: landscape)"} srcSet={PngLg} type={"image/png"} />
          <img src={PngLg} alt="Hand reaching to tap tablet on keyboard" width={740} height={673} className={"image"} />
        </picture>
        <h1 className="overlay-text">Email for communications</h1>
      </header>
      <div className="content-and-navigation">
        <div className="content">
          <div className="page-header-text">Email for communications</div>
          <p className="instructions">
            Enter the email address where you would like to receive account notifications, instructions and purchase receipts.
          </p>
          <form onSubmit={handleContinue} className="form">
            <TextBox
              autoComplete={"email"}
              inputMode={"email"}
              isEmail={true}
              isErrorState={error !== null}
              errorMessages={error ? [error] : undefined}
              label={"Email address"}
              onChange={setEmail}
              value={email}
            />
            <Button
              isLoading={isLoading}
              buttonType={"secondary"}
              onClick={() => { void handleContinue(); }}
              text={isLoading ? "Continuing…" : "Continue"}
            />
          </form>
          <div className="navigation" style={{marginTop: 16}}>
            <Button
              buttonType={"primary"}
              onClick={() => {
                socialLinkHandoff.clear();
                routerNavigate(appRoutePaths.signInPage);
              }}
              text={"Back to sign in"}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
